import { useCallback, useState } from "react";

import { type CloudFlowNodeType } from "@doitintl/cmp-models";
import { Stack } from "@mui/material";
import { type Node, useReactFlow } from "@xyflow/react";

import { useDarkThemeCheck } from "../../../../Components/hooks/useDarkThemeCheck";
import { colors } from "../../../../muiTheme";
import { type RFNode } from "../../types";
import { useNodeEdgeManager } from "../Common/NodeEdgeManagerProvider";
import { CloudFlowStatusIndicator } from "./CloudFlowStatusIndicator";
import NodeErrorMenu from "./NodeErrorMenu";
import { type CloudFlowStatus } from "./type";

type Props = {
  nodesWithErrors: Node<RFNode<CloudFlowNodeType>>[];
  status: CloudFlowStatus;
};

export const CloudflowStatusBar = ({ nodesWithErrors, status }: Props) => {
  const { selectNode } = useNodeEdgeManager();
  const { fitView } = useReactFlow();
  const isDarkMode = useDarkThemeCheck();
  const [isExpanded, setIsExpanded] = useState(false);
  const color = isDarkMode ? colors.dark : colors.light;
  const background = status === "readyToPublish" ? color.success.background : color.info.background;
  const border = status === "readyToPublish" ? color.success.border : color.info.border;

  const handleErrorNodeClick = useCallback(
    (nodeId: string) => {
      selectNode(nodeId);
      fitView({ nodes: [{ id: nodeId }], duration: 800, maxZoom: 1, minZoom: 0.5 });
      setIsExpanded(false);
    },
    [selectNode, fitView]
  );

  const handleExpandToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setIsExpanded(false);
  }, []);

  return (
    <Stack
      data-cy="cloudflow-status-container"
      sx={{
        width: 250,
        maxHeight: 500,
        position: "absolute",
        top: 60,
        left: 150,
        transform: "translateX(-50%)",
        zIndex: 2,
      }}
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        data-cy="cloudflow-status-card"
        sx={{
          py: 0.5,
          px: 1,
          borderRadius: 2,
          border: `1px solid ${border}`,
          background,
          boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.15)",
        }}
      >
        <CloudFlowStatusIndicator
          onExpandClick={handleExpandToggle}
          errorNodesCount={nodesWithErrors.length}
          isExpanded={isExpanded}
          status={status}
        />
      </Stack>
      <NodeErrorMenu
        nodesWithErrors={nodesWithErrors}
        isExpanded={isExpanded}
        onErrorNodeClick={handleErrorNodeClick}
        onClose={handleClose}
      />
    </Stack>
  );
};
